import { AuthorizationScope } from '@/constants/authorization-scope';
import { environment } from '@/environments/environment';
import { Privilege } from '@/models/privilege';
import { Feature } from '@/models/system/feature';
import { Role } from '@/models/system/role';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { selectCurrentClientId } from '../store/selectors/client.selector';
import {
  selectLoggedInUser,
  selectLoggedInUserId,
} from '../store/selectors/logged-in-user.selector';
import { selectSelectedResellerId } from '../store/selectors/reseller.selectors';
import { SessionStorageService } from './session-storage.service';

interface Result {
  authorizationResult: boolean | null;
  storageKey?: string;
}

function makeStorageKey(
  userId: number,
  features: string[],
  privilegeMask: number,
  subject: string | number,
  authorizationScope: AuthorizationScope,
  mustMatchAllFeatures: boolean,
): string {
  const featuresKey = features.length
    ? features.sort().join('_')
    : 'nofeatures';
  return `roleservice_${userId}_${featuresKey}_${privilegeMask}_${subject}_${authorizationScope}_${mustMatchAllFeatures}`;
}

@Injectable({
  providedIn: 'root',
})
export class RolesService {
  private httpClient = inject(HttpClient);
  private store = inject(Store);
  private sessionStorageService = inject(SessionStorageService);

  public isUserAuthorized(
    features: string[],
    privilegeMask: number,
    authorizationScope: AuthorizationScope = AuthorizationScope.client,
    mustMatchAllFeatures = false,
    subject?: string | number,
  ): Observable<boolean> {
    if (features.length === 0) {
      return of(true);
    }
    const queryBase = this.store
      .select(selectLoggedInUserId)
      .pipe(filter((userId) => !!userId));
    let query: Observable<Result>;
    switch (authorizationScope) {
      case AuthorizationScope.client:
        query = queryBase.pipe(
          switchMap((userId) =>
            this.handleClient(
              userId,
              subject ?? null,
              features,
              privilegeMask,
              authorizationScope,
              mustMatchAllFeatures,
            ),
          ),
        );
        break;

      case AuthorizationScope.reseller:
        query = queryBase.pipe(
          switchMap((userId) =>
            this.handleReseller(
              userId,
              features,
              privilegeMask,
              authorizationScope,
              mustMatchAllFeatures,
              typeof subject === 'number' ? subject : undefined,
            ),
          ),
        );
        break;

      case AuthorizationScope.system:
      default:
        query = queryBase.pipe(
          switchMap((userId) =>
            this.handleSystem(
              userId,
              features,
              privilegeMask,
              authorizationScope,
              mustMatchAllFeatures,
            ),
          ),
        );
        break;
    }

    return query.pipe(
      tap(({ authorizationResult, storageKey }) => {
        if (storageKey && authorizationResult !== null) {
          this.sessionStorageService.setItem(storageKey, authorizationResult);
        }
      }),
      map(({ authorizationResult }) => authorizationResult === true),
    );
  }

  public isUserRelationshipAuthorized(
    targetUserId: number,
    privilegeMask: number,
  ) {
    return this.store.select(selectLoggedInUserId).pipe(
      filter((userId) => !!userId),
      switchMap((userId) => {
        const storageKey = `roleservice_usertouser_${userId}_${targetUserId}_${privilegeMask}`;
        const cachedResult = this.sessionStorageService.getItem(storageKey);
        if (cachedResult !== null) {
          return of<Result>({
            storageKey,
            authorizationResult: cachedResult as boolean,
          });
        } else {
          return this.fetchUserRelationshipAuthorization(
            userId,
            targetUserId,
            privilegeMask,
          ).pipe(
            map((authorizationResult) => ({
              authorizationResult,
              storageKey,
            })),
          );
        }
      }),
      tap(({ authorizationResult, storageKey }) => {
        if (storageKey && authorizationResult !== null) {
          this.sessionStorageService.setItem(storageKey, authorizationResult);
        }
      }),
      map(({ authorizationResult }) => authorizationResult === true),
    );
  }

  public loadFeatures() {
    const url = new URL('features', environment.serviceEndpoints.relationships);

    return this.httpClient.get<Feature[]>(url.toString());
  }

  loadPrivileges() {
    const url = new URL(
      'privileges',
      environment.serviceEndpoints.relationships,
    );

    return this.httpClient.get<Privilege[]>(url.toString());
  }

  loadRoles() {
    const url = new URL('roles', environment.serviceEndpoints.relationships);

    return this.httpClient.get<Role[]>(url.toString());
  }

  fetchUserAuthorization(
    userId: number,
    features: string[],
    privilegeMask: number,
    authorizationScope: AuthorizationScope,
    subject?: string | number,
    mustMatchAllFeatures = false,
  ): Observable<boolean | null> {
    const url = new URL('authorization', environment.serviceEndpoints.users);
    // The 'userId' query parameter is not consumed by the API but is sent for cache busting.
    url.searchParams.append('userId', userId.toString());
    url.searchParams.append('privileges', privilegeMask.toString());
    for (const feature of features) {
      url.searchParams.append('feature', feature);
    }
    url.searchParams.append('scope', authorizationScope.toString());
    if (subject) {
      url.searchParams.append('subject', subject.toString());
    }
    if (mustMatchAllFeatures) {
      url.searchParams.append('mustMatchAllFeatures', 'true');
    }

    return this.httpClient.get(url.toString(), { observe: 'response' }).pipe(
      map((response) => response.status === 204),
      catchError((e) => {
        if (e.status && e.status === 403) {
          return of(false);
        } else {
          return of(null);
        }
      }),
    );
  }

  fetchUserRelationshipAuthorization(
    userId: number,
    targetUserId: number,
    privilegeMask: number,
  ): Observable<boolean | null> {
    const url = new URL(
      'authorization/relationship',
      environment.serviceEndpoints.users,
    );

    url.searchParams.append('subjectUserId', userId.toString());
    url.searchParams.append('targetUserId', targetUserId.toString());
    url.searchParams.append('privileges', privilegeMask.toString());

    return this.httpClient.get(url.toString(), { observe: 'response' }).pipe(
      map((response) => response.status === 204),
      catchError((e) => {
        if (e.status && e.status === 403) {
          return of(false);
        } else {
          return of(null);
        }
      }),
    );
  }

  private handleSystem(
    userId: number,
    features: string[],
    privilegeMask: number,
    authorizationScope: AuthorizationScope,
    mustMatchAllFeatures: boolean,
  ): Observable<Result> {
    const storageKey = makeStorageKey(
      userId,
      features,
      privilegeMask,
      'system',
      authorizationScope,
      mustMatchAllFeatures,
    );
    const cachedResult = this.sessionStorageService.getItem(storageKey);
    if (cachedResult !== null) {
      return of({
        authorizationResult: cachedResult as boolean,
        storageKey,
      });
    } else {
      return this.fetchUserAuthorization(
        userId,
        features,
        privilegeMask,
        authorizationScope,
        undefined,
        mustMatchAllFeatures,
      ).pipe(
        map((authorizationResult) => ({ authorizationResult, storageKey })),
      );
    }
  }

  private handleReseller(
    userId: number,
    features: string[],
    privilegeMask: number,
    authorizationScope: AuthorizationScope,
    mustMatchAllFeatures: boolean,
    resellerId?: number,
  ) {
    const resellerId$ = resellerId
      ? of<number>(resellerId)
      : (this.store
          .select(selectSelectedResellerId)
          .pipe(filter((id) => id !== null)) as Observable<number>);
    return resellerId$.pipe(
      map((resellerId) => {
        if (!resellerId) {
          return { resellerId: null, storageKey: undefined };
        } else {
          return {
            resellerId: resellerId,
            storageKey: makeStorageKey(
              userId,
              features,
              privilegeMask,
              resellerId,
              authorizationScope,
              mustMatchAllFeatures,
            ),
          };
        }
      }),
      switchMap(({ resellerId, storageKey }) => {
        if (!resellerId) {
          return of({
            authorizationResult: false,
          });
        }
        const cachedResult = this.sessionStorageService.getItem(storageKey);
        if (cachedResult !== null) {
          return of({
            authorizationResult: cachedResult as boolean,
            storageKey,
          });
        } else {
          return this.fetchUserAuthorization(
            userId,
            features,
            privilegeMask,
            authorizationScope,
            resellerId!,
            mustMatchAllFeatures,
          ).pipe(
            map((authorizationResult) => ({ authorizationResult, storageKey })),
          );
        }
      }),
    );
  }

  private handleClient(
    userId: number,
    clientId: number | string | null,
    features: string[],
    privilegeMask: number,
    subjectType: AuthorizationScope,
    mustMatchAllFeatures: boolean,
  ): Observable<Result> {
    const clientId$ =
      clientId !== null
        ? of<number | string | null>(clientId)
        : this.store.select(selectCurrentClientId);

    return clientId$.pipe(
      concatLatestFrom(() => [this.store.select(selectLoggedInUser)]),
      switchMap(([clientId, user]) => {
        if (
          clientId === null &&
          ((user?.resellerRoles.length ?? 0) > 0 ||
            (user?.systemRoles.length ?? 0) > 0)
        ) {
          return of<Result>({
            authorizationResult: true,
            storageKey: makeStorageKey(
              userId,
              features,
              privilegeMask,
              -1,
              subjectType,
              mustMatchAllFeatures,
            ),
          });
        } else {
          return this.checkPermissionForClient(
            userId,
            clientId!,
            features,
            privilegeMask,
            subjectType,
            mustMatchAllFeatures,
            makeStorageKey(
              userId,
              features,
              privilegeMask,
              clientId!,
              subjectType,
              mustMatchAllFeatures,
            ),
          );
        }
      }),
    );
  }

  private checkPermissionForClient(
    userId: number,
    clientId: number | string,
    features: string[],
    privilegeMask: number,
    subjectType: AuthorizationScope,
    mustMatchAllFeatures: boolean,
    storageKey: string,
  ) {
    const cachedResult = this.sessionStorageService.getItem(storageKey);
    if (cachedResult !== null) {
      return of({
        authorizationResult: cachedResult as boolean,
        storageKey,
      });
    } else {
      return this.fetchUserAuthorization(
        userId,
        features,
        privilegeMask,
        subjectType,
        clientId!,
        mustMatchAllFeatures,
      ).pipe(
        map((authorizationResult) => ({ authorizationResult, storageKey })),
      );
    }
  }
}
